import type { AggregatedOrderView } from '@/types/api/aggregated/order'
import type { AggregatedRfqView } from '@/types/api/aggregated/rfq'

import { useLogger } from '@moment/observability/react'
import { type RefObject, useCallback, useEffect, useRef } from 'react'

import { TradingPermissions } from '@/config/permissions/trading-permissions'
import useHasPermissions from '@/hooks/use-has-permissions'
import { createLogger } from '@/lib/logger'
import { handleRfqWebSocketMessage } from '@/utils/trade-blotter/rfq-stream/rfq-update'
import { handleWebSocketMessage } from '@/utils/trade-blotter/trading-events/helpers'
import { manageWebSocketLifecycle } from '@/utils/trade-blotter/web-socket'

const LOGGER = createLogger(
  'components/trade-desk/utils/use-connect-to-websockets',
)

export const RFQ_EVENTS_HWM_KEY = 'rfq-events-hwm'
export const RFQ_EVENTS_WS_ROUTE = 'trading/rfq-stream'
export const TRADING_EVENTS_WS_ROUTE = 'trading/order-stream'
export const TRADING_EVENTS_HWM_KEY = 'trading-events-hwm'

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function useConnectToWebsockets(
  tradingEventsHwm: bigint,
  rfqEventsHwm: bigint,
  aggregatedOrderViewsRef: RefObject<Map<string, AggregatedOrderView>>,
  aggregatedRfqViewsRef: RefObject<Map<string, AggregatedRfqView>>,
  onUpdateApplied: ({
    toAdd,
    toUpdate,
    toRemove,
  }: {
    toAdd?: AggregatedOrderView[]
    toUpdate?: AggregatedOrderView[]
    toRemove?: AggregatedOrderView[]
  }) => void,
  onRfqUpdateApplied: ({
    toUpdate,
  }: {
    toUpdate?: AggregatedRfqView[]
  }) => void,
  bootstrapCompleted: boolean,
  enabled: boolean = true,
) {
  const logger = useLogger()
  const hasRfqReadPermissions = useHasPermissions(TradingPermissions.RFQ_READ)

  const tradingEventsCleanup = useRef<() => void>(() => {})
  const rfqEventsCleanup = useRef<() => void>(() => {})
  useEffect(() => {
    return () => {
      LOGGER.debug('cleaning up websocket')
      tradingEventsCleanup.current()
      rfqEventsCleanup.current()
    }
  }, [])

  const initializeTradingEventsWebSocket = useCallback(() => {
    localStorage.setItem(
      `${window.location.pathname}/${TRADING_EVENTS_HWM_KEY}`,
      tradingEventsHwm.toString(),
    )
    const cleanup = manageWebSocketLifecycle(
      tradingEventsHwm,
      TRADING_EVENTS_HWM_KEY,
      TRADING_EVENTS_WS_ROUTE,
      handleWebSocketMessage,
      aggregatedOrderViewsRef,
      onUpdateApplied,
      undefined, // shouldRemove
      undefined, // time
    )
    tradingEventsCleanup.current = cleanup
  }, [tradingEventsHwm, aggregatedOrderViewsRef, onUpdateApplied])

  const initializeRfqWebSocket = useCallback(() => {
    localStorage.setItem(
      `${window.location.pathname}/${RFQ_EVENTS_HWM_KEY}`,
      rfqEventsHwm.toString(),
    )
    const cleanup = manageWebSocketLifecycle(
      rfqEventsHwm,
      RFQ_EVENTS_HWM_KEY,
      RFQ_EVENTS_WS_ROUTE,
      handleRfqWebSocketMessage,
      aggregatedRfqViewsRef,
      onRfqUpdateApplied,
      undefined, // shouldRemove
      undefined, // time
    )
    rfqEventsCleanup.current = cleanup
  }, [rfqEventsHwm, aggregatedRfqViewsRef, onRfqUpdateApplied])

  const hasInitializedWebsockets = useRef(false)
  useEffect(() => {
    if (enabled && bootstrapCompleted && !hasInitializedWebsockets.current) {
      initializeTradingEventsWebSocket()
      if (hasRfqReadPermissions) {
        initializeRfqWebSocket()
      } else {
        logger.info(
          'No RFQ read permissions, skipping RFQ websocket initialization',
        )
      }
      hasInitializedWebsockets.current = true
    }
  }, [
    enabled,
    bootstrapCompleted,
    initializeTradingEventsWebSocket,
    initializeRfqWebSocket,
    hasRfqReadPermissions,
    logger,
  ])
}
